import { createAsyncThunk } from "@reduxjs/toolkit";
import { type PackAccountsContextItem } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums";
import type { AppDispatch, AppThunk, RootState } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import { type FetchSuccessPayload } from "features/Application/slices/createFetchingItemsSlice";
import { getPrefix } from "features/Application/slices/models";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type GetAvailableTemplatesRequest, type TemplatePacks } from "features/People/GroupTemplate/DataService/types";
import { type DistributedOpUpdateParams, type UpdateParams } from "interfaces";
import dateTimeUtils from "utils/dateTimeUtils";
import { formatFilters } from "utils/filterMapUtils";
import { FilterType } from "utils/filterUtils";
import { escapeTerm } from "utils/searchUtils";
import { pluralize } from "utils/stringUtils";
import { type AvailableTemplate } from "../../CreateAccount/types";

export const fetchAvailableTemplates = createAsyncThunk<
  FetchSuccessPayload<AvailableTemplate>,
  GetAvailableTemplatesRequest
>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Accounts,
    entity: ReducerEntityPrefixTypes.AccountCreation,
    name: "templates",
  }),
  async (req: GetAvailableTemplatesRequest, { getState }) => {
    const filtersState = (getState() as RootState).accounts.availableTemplates.filters;
    const result = await DataService.groupTemplates.getAvailableTemplates({
      ...req,
      term: escapeTerm(filtersState.search),
      ...formatFilters(filtersState.appliedFilter, {
        dateCreated: FilterType.DateRange,
      }),
    });

    return { items: result.items, totalCount: result.count };
  },
);

export const addTemplatesThunk =
  (accountId: number, templates: number[], autoApprove: boolean = false): AppThunk =>
  async (dispatch) => {
    if (templates.length === 0) return;
    try {
      const multi = templates.length > 1;
      const updateParams: UpdateParams = {
        id: "AddCustomerTemplates",
        title: `Adding templates`,
        getMessageIds: async () => {
          const response = await DataService.accounts.addTemplates(accountId, templates, autoApprove);
          return response.messageIds;
        },
        successTransientMessage: `${multi ? "Templates have" : "Template has"} been added to this customer successfully.`,
        successTitle: `${multi ? "Templates" : "Template"} added!`,
        failureTransientMessage: `Adding customers failed!`,
      };
      await backgroundTask.initializeBackgroundTask(updateParams, dispatch);
    } catch {}
  };

export const getTemplatesPacks = (templateIds: number[]) => async () => {
  const result = await DataService.groupTemplates.getTemplatesPacks(templateIds);
  return result.data;
};

export const saveTemplatesPacks = (accountId: number, packs: PackAccountsContextItem[]) => {
  return async (dispatch: AppDispatch) => {
    if (packs.length === 0) return;

    const templatesPacks: TemplatePacks[] = packs.map((p) => {
      return {
        packId: p.id,
        templateLicenses: p.templateIds?.length
          ? p.templateIds.map((id) => ({
              templateId: id,
              licenseTypeId: p.licenseTypeId!,
              expirationDate: p.expirationDate
                ? new Date(dateTimeUtils.toServerFormatDate(p.expirationDate))
                : undefined,
              licensesCount: 1,
            }))
          : [],
      };
    });

    const getOperationProps = async () => {
      const data = await DataService.groupTemplates.saveTemplatesPacks(accountId, templatesPacks);
      return data;
    };

    const updateParams: DistributedOpUpdateParams = {
      id: "AddTemplatePacksToAccount",
      title: `Add ${pluralize("template", packs.length)} to account`,
      getOperationProps,
      indeterminate: true,
      successTransientMessage: `${pluralize("Template", packs.length)} has been added to account successfully`,
      failureTransientMessage: `Adding ${pluralize("template", packs.length)} to account failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};
